import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

export const PIN_MIN_LENGTH = 4;
export const PIN_MAX_LENGTH = 6;

/**
 * 4-6 digit PIN entry: one hidden real input (so focus, IME, paste and password managers all
 * behave natively) rendered behind a row of styled digit boxes. Auto-submits when the 6th
 * digit lands; Enter submits at 4-5 digits. Paste is filtered to digits. No OTP dependency —
 * the repo intentionally has none.
 *
 * Controlled: `value` (digits string) + `onChange`. `shakeSignal` is a counter the owner
 * bumps to replay the error shake (the key change restarts the CSS animation).
 */
export function PinInput({
  value = "",
  onChange,
  onSubmit,
  disabled = false,
  autoFocus = false,
  error = false,
  shakeSignal = 0,
  ariaLabel = "PIN",
}) {
  const inputRef = useRef(null);
  const [focused, setFocused] = useState(false);

  // Disabling the input (during a submit / lockout countdown) blurs it and re-enabling does
  // NOT restore focus — without this, every wrong attempt would force a click before retyping
  // on the hottest path of the lock screen.
  const wasDisabledRef = useRef(disabled);
  useEffect(() => {
    if (wasDisabledRef.current && !disabled) {
      inputRef.current?.focus();
    }
    wasDisabledRef.current = disabled;
  }, [disabled]);

  const digits = String(value || "").replace(/\D/g, "").slice(0, PIN_MAX_LENGTH);

  const handleChange = (event) => {
    if (disabled) return;
    const next = String(event.target.value || "").replace(/\D/g, "").slice(0, PIN_MAX_LENGTH);
    onChange?.(next);
    if (next.length === PIN_MAX_LENGTH && next !== digits) {
      onSubmit?.(next);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    if (!disabled && digits.length >= PIN_MIN_LENGTH) {
      onSubmit?.(digits);
    }
  };

  return (
    <div
      className={cn("relative inline-flex", disabled && "opacity-50")}
      onClick={() => inputRef.current?.focus()}
    >
      <input
        ref={inputRef}
        // type="password" so the typed PIN never sits as plaintext in the DOM value /
        // accessibility tree (screen readers announce bullets, extensions can't scrape it);
        // the visible bullet boxes below are purely decorative.
        type="password"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        aria-label={ariaLabel}
        value={digits}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={disabled}
        autoFocus={autoFocus}
        // The real input stays interactive but invisible, stretched over the boxes so a click
        // anywhere in the row focuses it; caret/selection visuals come from the boxes below.
        className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
      />
      <div key={shakeSignal} className={cn("flex items-center gap-2", error && shakeSignal > 0 && "pin-shake")} aria-hidden>
        {Array.from({ length: PIN_MAX_LENGTH }, (_, index) => {
          const filled = index < digits.length;
          const isCaret = focused && index === digits.length;
          return (
            <div
              key={index}
              className={cn(
                "flex h-11 w-9 items-center justify-center rounded-md border border-border bg-background text-lg font-semibold text-foreground transition-colors",
                // The 5th/6th boxes are optional (PIN may be 4-6 digits) — render them fainter.
                index >= PIN_MIN_LENGTH && !filled && "border-dashed opacity-60",
                isCaret && "border-ring ring-2 ring-ring/40",
                error && "border-destructive",
              )}
            >
              {filled ? "•" : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
}
